import React, { useState } from 'react';
import { X, FileText, Loader2, Download, ChevronRight, ChevronLeft, Sparkles, RefreshCw } from 'lucide-react';
import { useGenerateSarNarrative, useGenerateSarReport } from '../../hooks/useCases';

const SUBJECT_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'entity', label: 'Business Entity' },
];

const ACTIVITY_TYPES = [
  { value: 'structuring', label: 'Structuring / Smurfing' },
  { value: 'money_laundering', label: 'Money Laundering' },
  { value: 'terrorist_financing', label: 'Terrorist Financing' },
  { value: 'fraud', label: 'Fraud' },
  { value: 'bribery', label: 'Bribery / Corruption' },
  { value: 'other', label: 'Other Suspicious Activity' },
];

const INSTRUMENTS = [
  { value: 'cash', label: 'Cash' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'check', label: 'Check / Money Order' },
  { value: 'crypto', label: 'Cryptocurrency' },
  { value: 'ach', label: 'ACH Transfer' },
  { value: 'card', label: 'Debit/Credit Card' },
  { value: 'prepaid', label: 'Prepaid Card' },
];

export default function SarModal({ isOpen, onClose, caseData }) {
  const [step, setStep] = useState(1);
  const [activityDetails, setActivityDetails] = useState({
    subject_type: 'individual',
    activity_type: 'fraud',
    activity_start_date: '',
    activity_end_date: '',
    total_amount: '',
    currency: 'USD',
    instruments: [],
    additional_context: '',
  });
  const [narrative, setNarrative] = useState('');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [error, setError] = useState(null);

  const generateNarrative = useGenerateSarNarrative();
  const generateReport = useGenerateSarReport();

  if (!isOpen) return null;

  const handleActivityChange = (field, value) => {
    setActivityDetails((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const toggleInstrument = (instrument) => {
    setActivityDetails((prev) => ({
      ...prev,
      instruments: prev.instruments.includes(instrument)
        ? prev.instruments.filter((i) => i !== instrument)
        : [...prev.instruments, instrument],
    }));
  };

  const handleGenerateNarrative = async () => {
    setError(null);
    const payload = {
      ...activityDetails,
      activity_start_date: activityDetails.activity_start_date || null,
      activity_end_date: activityDetails.activity_end_date || null,
      total_amount: activityDetails.total_amount ? parseFloat(activityDetails.total_amount) : null,
    };

    try {
      const result = await generateNarrative.mutateAsync({
        caseId: caseData.id,
        activityDetails: payload,
      });
      setNarrative(result.narrative);
      setStep(2);
    } catch (err) {
      setError(err?.message || 'Failed to generate narrative');
    }
  };

  const handleGenerateReport = async () => {
    setError(null);
    const payload = {
      ...activityDetails,
      activity_start_date: activityDetails.activity_start_date || null,
      activity_end_date: activityDetails.activity_end_date || null,
      total_amount: activityDetails.total_amount ? parseFloat(activityDetails.total_amount) : null,
    };

    try {
      const blob = await generateReport.mutateAsync({
        caseId: caseData.id,
        narrative,
        activityDetails: payload,
      });
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);
      setReferenceNumber(`SAR-${caseData.case_number}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`);
      setStep(3);
    } catch (err) {
      setError(err?.message || 'Failed to generate report');
    }
  };

  const handleDownload = () => {
    if (!downloadUrl) return;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${referenceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setStep(1);
    setNarrative('');
    setDownloadUrl(null);
    setReferenceNumber('');
    setError(null);
    onClose();
  };

  const wordCount = narrative ? narrative.split(/\s+/).filter(Boolean).length : 0;

  return (
    <div className="sar-modal-overlay" onClick={handleClose} data-testid="sar-modal">
      <style>{`
        .sar-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .sar-modal {
          background: var(--bg-primary, #1a1b23);
          border: 1px solid var(--border-color, #2a2f3a);
          border-radius: 12px;
          width: 100%;
          max-width: 640px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .sar-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-color, #2a2f3a);
        }

        .sar-modal-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sar-modal-close {
          background: none;
          border: none;
          color: var(--text-secondary, #8b919e);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
        }

        .sar-modal-close:hover {
          color: var(--text-primary, #f0f2f5);
        }

        .sar-steps {
          display: flex;
          gap: 4px;
          padding: 12px 20px;
          border-bottom: 1px solid var(--border-subtle, rgba(255,255,255,0.04));
        }

        .sar-step {
          flex: 1;
          height: 3px;
          border-radius: 2px;
          background: var(--bg-elevated, rgba(255,255,255,0.08));
          transition: background 0.2s;
        }

        .sar-step.active {
          background: var(--accent-primary, #14b8a6);
        }

        .sar-step.completed {
          background: var(--status-approved, #34d399);
        }

        .sar-modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
        }

        .sar-section-label {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin-bottom: 12px;
        }

        .sar-form-group {
          margin-bottom: 16px;
        }

        .sar-form-label {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary, #8b919e);
          margin-bottom: 6px;
          display: block;
        }

        .sar-form-input,
        .sar-form-select,
        .sar-form-textarea {
          width: 100%;
          background: var(--bg-surface, rgba(255,255,255,0.03));
          border: 1px solid var(--border-subtle, rgba(255,255,255,0.1));
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 13px;
          color: var(--text-primary, #f0f2f5);
          outline: none;
          transition: border-color 0.15s;
        }

        .sar-form-input:focus,
        .sar-form-select:focus,
        .sar-form-textarea:focus {
          border-color: var(--accent-primary, #14b8a6);
        }

        .sar-form-select option {
          background: var(--bg-primary, #1a1b23);
        }

        .sar-form-textarea {
          min-height: 100px;
          resize: vertical;
          font-family: inherit;
          line-height: 1.5;
        }

        .sar-narrative-textarea {
          min-height: 300px;
          line-height: 1.6;
        }

        .sar-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .sar-instruments-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .sar-instrument-chip {
          padding: 5px 12px;
          border-radius: 16px;
          font-size: 12px;
          border: 1px solid var(--border-subtle, rgba(255,255,255,0.1));
          background: transparent;
          color: var(--text-secondary, #8b919e);
          cursor: pointer;
          transition: all 0.15s;
        }

        .sar-instrument-chip.selected {
          background: rgba(20, 184, 166, 0.1);
          border-color: var(--accent-primary, #14b8a6);
          color: var(--accent-primary, #14b8a6);
        }

        .sar-word-count {
          font-size: 11px;
          color: var(--text-tertiary, #5a5f6b);
          text-align: right;
          margin-top: 6px;
        }

        .sar-narrative-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .sar-regen-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 5px 10px;
          background: none;
          border: 1px solid var(--border-subtle, rgba(255,255,255,0.1));
          border-radius: 5px;
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
          cursor: pointer;
        }

        .sar-regen-btn:hover {
          border-color: var(--accent-primary, #14b8a6);
          color: var(--accent-primary, #14b8a6);
        }

        .sar-modal-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 20px;
          border-top: 1px solid var(--border-color, #2a2f3a);
        }

        .sar-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: opacity 0.15s;
          border: none;
        }

        .sar-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .sar-btn-primary {
          background: var(--accent-primary, #14b8a6);
          color: white;
        }

        .sar-btn-secondary {
          background: transparent;
          color: var(--text-secondary, #8b919e);
          border: 1px solid var(--border-subtle, rgba(255,255,255,0.1));
        }

        .sar-btn-success {
          background: var(--status-approved, #34d399);
          color: #1a1b23;
        }

        .sar-error {
          font-size: 12px;
          color: var(--status-rejected, #f87171);
          margin-top: 8px;
        }

        .sar-success-section {
          text-align: center;
          padding: 30px 0;
        }

        .sar-success-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(52, 211, 153, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: var(--status-approved, #34d399);
        }

        .sar-success-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin-bottom: 8px;
        }

        .sar-success-ref {
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
          margin-bottom: 20px;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="sar-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sar-modal-header">
          <div className="sar-modal-title">
            <FileText size={18} />
            File SAR Report
          </div>
          <button className="sar-modal-close" onClick={handleClose}>
            <X size={18} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="sar-steps">
          <div className={`sar-step ${step >= 1 ? (step > 1 ? 'completed' : 'active') : ''}`} />
          <div className={`sar-step ${step >= 2 ? (step > 2 ? 'completed' : 'active') : ''}`} />
          <div className={`sar-step ${step >= 3 ? 'active' : ''}`} />
        </div>

        {/* Body */}
        <div className="sar-modal-body">
          {/* Step 1: Activity Details */}
          {step === 1 && (
            <div data-testid="sar-step-1">
              <div className="sar-section-label">Subject Information</div>

              <div className="sar-form-group">
                <label className="sar-form-label">Subject Type</label>
                <div className="sar-instruments-grid">
                  {SUBJECT_TYPES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      className={`sar-instrument-chip ${activityDetails.subject_type === t.value ? 'selected' : ''}`}
                      onClick={() => handleActivityChange('subject_type', t.value)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="sar-section-label" style={{ marginTop: 20 }}>Activity Details</div>

              <div className="sar-form-group">
                <label className="sar-form-label">Activity Type</label>
                <select
                  className="sar-form-select"
                  value={activityDetails.activity_type}
                  onChange={(e) => handleActivityChange('activity_type', e.target.value)}
                >
                  {ACTIVITY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="sar-form-row">
                <div className="sar-form-group">
                  <label className="sar-form-label">Start Date</label>
                  <input
                    type="date"
                    className="sar-form-input"
                    value={activityDetails.activity_start_date}
                    onChange={(e) => handleActivityChange('activity_start_date', e.target.value)}
                  />
                </div>
                <div className="sar-form-group">
                  <label className="sar-form-label">End Date</label>
                  <input
                    type="date"
                    className="sar-form-input"
                    value={activityDetails.activity_end_date}
                    onChange={(e) => handleActivityChange('activity_end_date', e.target.value)}
                  />
                </div>
              </div>

              <div className="sar-form-row">
                <div className="sar-form-group">
                  <label className="sar-form-label">Total Amount</label>
                  <input
                    type="number"
                    className="sar-form-input"
                    placeholder="0.00"
                    value={activityDetails.total_amount}
                    onChange={(e) => handleActivityChange('total_amount', e.target.value)}
                  />
                </div>
                <div className="sar-form-group">
                  <label className="sar-form-label">Currency</label>
                  <select
                    className="sar-form-select"
                    value={activityDetails.currency}
                    onChange={(e) => handleActivityChange('currency', e.target.value)}
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CHF">CHF</option>
                  </select>
                </div>
              </div>

              <div className="sar-form-group">
                <label className="sar-form-label">Instruments</label>
                <div className="sar-instruments-grid">
                  {INSTRUMENTS.map((inst) => (
                    <button
                      key={inst.value}
                      type="button"
                      className={`sar-instrument-chip ${activityDetails.instruments.includes(inst.value) ? 'selected' : ''}`}
                      onClick={() => toggleInstrument(inst.value)}
                    >
                      {inst.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="sar-form-group">
                <label className="sar-form-label">Additional Context (Optional)</label>
                <textarea
                  className="sar-form-textarea"
                  placeholder="Any additional information for the narrative..."
                  value={activityDetails.additional_context}
                  onChange={(e) => handleActivityChange('additional_context', e.target.value)}
                  rows={3}
                />
              </div>

              {error && <div className="sar-error">{error}</div>}
            </div>
          )}

          {/* Step 2: Narrative Review */}
          {step === 2 && (
            <div data-testid="sar-step-2">
              <div className="sar-narrative-header">
                <div className="sar-section-label" style={{ marginBottom: 0 }}>
                  AI-Generated Narrative
                </div>
                <button
                  className="sar-regen-btn"
                  onClick={handleGenerateNarrative}
                  disabled={generateNarrative.isPending}
                >
                  <RefreshCw size={12} />
                  Regenerate
                </button>
              </div>

              <textarea
                className="sar-form-textarea sar-narrative-textarea"
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
              />
              <div className="sar-word-count">{wordCount} words</div>

              {error && <div className="sar-error">{error}</div>}
            </div>
          )}

          {/* Step 3: Success / Download */}
          {step === 3 && (
            <div data-testid="sar-step-3">
              <div className="sar-success-section">
                <div className="sar-success-icon">
                  <FileText size={24} />
                </div>
                <div className="sar-success-title">SAR Report Generated</div>
                <div className="sar-success-ref">Reference: {referenceNumber}</div>
                <button
                  className="sar-btn sar-btn-success"
                  onClick={handleDownload}
                >
                  <Download size={14} />
                  Download PDF
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sar-modal-footer">
          <div>
            {step > 1 && step < 3 && (
              <button
                className="sar-btn sar-btn-secondary"
                onClick={() => setStep(step - 1)}
              >
                <ChevronLeft size={14} />
                Back
              </button>
            )}
          </div>
          <div>
            {step === 1 && (
              <button
                className="sar-btn sar-btn-primary"
                onClick={handleGenerateNarrative}
                disabled={generateNarrative.isPending}
              >
                {generateNarrative.isPending ? (
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Sparkles size={14} />
                )}
                {generateNarrative.isPending ? 'Generating...' : 'Generate Narrative'}
              </button>
            )}
            {step === 2 && (
              <button
                className="sar-btn sar-btn-primary"
                onClick={handleGenerateReport}
                disabled={generateReport.isPending || wordCount < 50}
              >
                {generateReport.isPending ? (
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <ChevronRight size={14} />
                )}
                {generateReport.isPending ? 'Generating PDF...' : 'Generate Report'}
              </button>
            )}
            {step === 3 && (
              <button
                className="sar-btn sar-btn-secondary"
                onClick={handleClose}
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
